import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore, collection, doc, getDoc, getDocs, onSnapshot,
  query, where, orderBy, limit, Timestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import SetupGuard from '../components/SetupGuard';

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  seated: 'Seated',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
};

const STATUS_COLORS = {
  pending: 'warning',
  confirmed: 'success',
  seated: 'primary',
  completed: 'secondary',
  cancelled: 'danger',
  no_show: 'dark',
};

/**
 * Merchant reservations module — industry-standard day view (Clover / Toast
 * style): date picker, slot-ordered list, one-tap status actions (confirm /
 * seat / complete / cancel / no-show) and walk-in creation.
 */
const ReservationsPage = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();
  const functions = getFunctions();

  const [user, setUser] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [busyId, setBusyId] = useState(null);
  const [seatTarget, setSeatTarget] = useState(null); // reservation being seated (modal)
  const [searchQuery, setSearchQuery] = useState('');
  const [viewIndex, setViewIndex] = useState(0); // 0 = Reservations, 1 = Queue
  const [waitlist, setWaitlist] = useState([]);
  const [showNewReservation, setShowNewReservation] = useState(false);

  // Resolve user + selected location (same pattern as KDSStationsPage)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/admin/login');
        return;
      }
      setUser(currentUser);
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const selectedId = userDoc.data()?.selectedLocationId;
      if (!selectedId) {
        Swal.fire('Info', 'Please select a location first', 'info');
        navigate('/admin/locations');
        return;
      }
      setLocationId(selectedId);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time listener for the selected day
  useEffect(() => {
    if (!user || !locationId) return;
    setLoading(true);
    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    const q = query(
      collection(db, 'users', user.uid, 'locations', locationId, 'reservations'),
      where('dateTime', '>=', Timestamp.fromDate(dayStart)),
      where('dateTime', '<', Timestamp.fromDate(dayEnd)),
      orderBy('dateTime', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setReservations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('Reservations listener error:', err);
      setLoading(false);
    });
    return unsub;
  }, [user, locationId, selectedDate, db]);

  // Real-time listener for the walk-in queue (active entries)
  useEffect(() => {
    if (!user || !locationId) return;
    const q = query(
      collection(db, 'users', user.uid, 'locations', locationId, 'waitlist'),
      where('status', 'in', ['waiting', 'notified']),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setWaitlist(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('Waitlist listener error:', err));
    return unsub;
  }, [user, locationId, db]);

  const updateStatus = useCallback(async (reservation, status, extra = {}) => {
    setBusyId(reservation.id);
    try {
      const fn = httpsCallable(functions, 'updateReservationStatus');
      const { data } = await fn({
        businessId: user.uid,
        locationId,
        reservationId: reservation.id,
        status,
        ...extra,
      });
      if (status === 'cancelled' && data.refunded) {
        Swal.fire('Refunded', 'The prepaid amount was fully refunded to the customer.', 'success');
      }
    } catch (e) {
      Swal.fire('Error', e.message || 'Action failed', 'error');
    } finally {
      setBusyId(null);
    }
  }, [functions, user, locationId]);

  const handleSeat = (reservation) => {
    // Floor-plan table picker (same data flow as open tabs): sections +
    // tables with reserved/occupied states and auto-assign.
    setSeatTarget(reservation);
  };

  // Seating an UNPAID reservation must open a real tab for the table (same
  // behavior as POS/handheld seating): the preorder items reach the kitchen
  // and the table becomes occupied. PREPAID needs no tab — the linked sale
  // already carries the table and the backend trigger fires it to the
  // kitchen (see onReservationUpdated).
  const _seatReservationWithTab = async (reservation, tableId, tableName) => {
    setBusyId(reservation.id);
    try {
      const isPrepaid = reservation.paymentStatus === 'prepaid';
      const linkedSaleId = reservation.preorderSaleId || reservation.prepaidSaleId || null;
      let openTabId = null;

      if (!isPrepaid) {
        // Reuse the linked preorder sale id when present — createOpenTabHttp
        // overwrites the parallel sale docs with the same id (no duplicates).
        openTabId = linkedSaleId || `rsv_${Date.now()}`;
        const preorderItems = Array.isArray(reservation.preorderItems) ? reservation.preorderItems : [];
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
        await fetch(`https://us-central1-${projectId}.cloudfunctions.net/createOpenTabHttp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uid: user.uid,
            localTabId: openTabId,
            confirmationNumber: reservation.confirmationNumber,
            items: preorderItems.map((it) => ({
              productId: it.productId,
              productName: it.productName,
              qty: it.qty,
              price: it.price,
              total: it.total,
              options: it.options || [],
              instructions: it.instructions,
              categoryId: it.categoryId,
              categoryName: it.categoryName,
            })),
            totals: {
              subtotal: preorderItems.reduce((s, it) => s + (it.total || 0), 0),
              tax: (reservation.preorderTotal || 0) - preorderItems.reduce((s, it) => s + (it.total || 0), 0),
              tip: 0, tipPercent: 0, tipIsPercent: false, discount: 0,
              total: reservation.preorderTotal || 0,
              itemsQty: preorderItems.reduce((s, it) => s + (it.qty || 1), 0),
            },
            customerInfo: {
              customerId: reservation.customerUid,
              fodyesUid: reservation.customerUid,
              customerName: reservation.customerName || 'Reservation Guest',
              customerEmail: reservation.customerEmail || '',
              customerPhone: reservation.customerPhone || '',
            },
            locationId,
            locationName: reservation.locationName || '',
            tabName: `${reservation.customerName || 'Reservation'} (Res ${reservation.confirmationNumber || ''})`,
            paymentMethod: 'define_later',
            tableId,
            tableName,
            orderType: 'dine_in',
            reservationId: reservation.id,
          }),
        });
      }

      await updateStatus(reservation, 'seated', { tableId, tableName, openTabId });
    } catch (e) {
      Swal.fire('Error', e.message || 'Seat failed', 'error');
      setBusyId(null);
    }
  };

  // ── Walk-in queue actions ────────────────────────────────────

  const updateWaitlistStatus = useCallback(async (entry, status, extra = {}) => {
    setBusyId(entry.id);
    try {
      const fn = httpsCallable(functions, 'updateWaitlistEntry');
      await fn({ businessId: user.uid, locationId, entryId: entry.id, status, ...extra });
    } catch (e) {
      Swal.fire('Error', e.message || 'Action failed', 'error');
    } finally {
      setBusyId(null);
    }
  }, [functions, user, locationId]);

  const handleAddToQueue = async () => {
    const { value: formValues } = await Swal.fire({
      title: 'Add party to queue',
      html: `
        <input id="swal-q-name" class="swal2-input" placeholder="Name *">
        <input id="swal-q-phone" class="swal2-input" placeholder="Phone (for SMS when ready)">
        <input id="swal-q-party" class="swal2-input" type="number" min="1" value="2" placeholder="Party size *">
        <textarea id="swal-q-notes" class="swal2-textarea" placeholder="Notes (optional)"></textarea>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Add to queue',
      confirmButtonColor: '#ff6b35',
      preConfirm: () => {
        const name = document.getElementById('swal-q-name').value.trim();
        const party = parseInt(document.getElementById('swal-q-party').value, 10);
        if (!name) return Swal.showValidationMessage('Name is required');
        if (!party || party < 1) return Swal.showValidationMessage('Party size is required');
        return {
          name,
          phone: document.getElementById('swal-q-phone').value.trim(),
          party,
          notes: document.getElementById('swal-q-notes').value.trim(),
        };
      },
    });
    if (!formValues) return;
    try {
      const fn = httpsCallable(functions, 'joinWaitlist');
      const { data } = await fn({
        businessId: user.uid,
        locationId,
        customerName: formValues.name,
        customerPhone: formValues.phone || null,
        partySize: formValues.party,
        notes: formValues.notes || null,
        source: 'admin',
      });
      if (data.success) {
        Swal.fire('Added', `~${data.quotedWaitMinutes} min wait · ${data.partiesAhead} ${data.partiesAhead === 1 ? 'party' : 'parties'} ahead.`, 'success');
      }
    } catch (e) {
      Swal.fire('Error', e.message || 'Could not add to the queue', 'error');
    }
  };

  const handleSeatQueueEntry = (entry) => {
    // Reuse the floor-plan SeatModal with a reservation-shaped object
    // (queue parties take any free table — no reservation window to check).
    setSeatTarget({
      id: entry.id,
      _isQueueEntry: true,
      customerName: entry.customerName,
      partySize: entry.partySize,
      dateTime: null,
      endDateTime: null,
      tableName: null,
    });
  };

  const _seatQueueEntryWithTab = async (entry, tableId, tableName) => {
    setBusyId(entry.id);
    try {
      // Create the open tab first (same flow as POS queue seating)
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
      const tabId = `wl_${Date.now()}`;
      await fetch(`https://us-central1-${projectId}.cloudfunctions.net/createOpenTabHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: user.uid,
          localTabId: tabId,
          items: [],
          totals: { subtotal: 0, tax: 0, tip: 0, tipPercent: 0, tipIsPercent: false, discount: 0, total: 0, itemsQty: 0 },
          customerInfo: { customerName: entry.customerName || 'Walk-in Guest', customerPhone: entry.customerPhone || '' },
          locationId,
          locationName: entry.locationName || '',
          tabName: `${entry.customerName || 'Guest'} (Queue)`,
          paymentMethod: 'define_later',
          tableId,
          tableName,
          orderType: 'dine_in',
        }),
      });
      const fn = httpsCallable(functions, 'updateWaitlistEntry');
      await fn({
        businessId: user.uid,
        entryId: entry.id,
        status: 'seated',
        tableId,
        tableName,
        openTabId: tabId,
      });
    } catch (e) {
      Swal.fire('Error', e.message || 'Seat failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (reservation) => {
    const prepaid = reservation.paymentStatus === 'prepaid';
    const result = await Swal.fire({
      title: 'Cancel reservation?',
      text: prepaid
        ? 'This reservation has a prepaid pre-order. Cancelling will fully refund the customer\'s card.'
        : `Cancel ${reservation.customerName || 'this customer'}'s reservation?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel',
      confirmButtonColor: '#dc3545',
    });
    if (result.isConfirmed) await updateStatus(reservation, 'cancelled');
  };

  const handleNoShow = async (reservation) => {
    const result = await Swal.fire({
      title: 'Mark as no-show?',
      text: `${reservation.customerName || 'This customer'} did not arrive.${reservation.paymentStatus === 'prepaid' ? ' The prepayment is NOT refunded automatically.' : ''}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'No-show',
      confirmButtonColor: '#343a40',
    });
    if (result.isConfirmed) await updateStatus(reservation, 'no_show');
  };

  const handleNewReservation = () => setShowNewReservation(true);

  const createWalkInReservation = async (formValues) => {
    try {
      const fn = httpsCallable(functions, 'createReservation');
      const { data } = await fn({
        businessId: user.uid,
        locationId,
        dateTime: new Date(formValues.time).toISOString(),
        partySize: formValues.party,
        notes: formValues.notes || null,
        customerName: formValues.name,
        customerEmail: formValues.email || null,
        customerPhone: formValues.phone || null,
        customerUid: formValues.customerUid,
        source: 'admin',
      });
      if (data.success) {
        Swal.fire('Created', `Reservation ${data.confirmationNumber} created (${data.status}).`, 'success');
      }
    } catch (e) {
      Swal.fire('Error', e.message || 'Could not create the reservation', 'error');
    }
  };

  const shiftDay = (delta) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + delta);
    setSelectedDate(d);
  };

  const isToday = selectedDate.toDateString() === new Date().toDateString();
  // Client-side search (name / phone / confirmation code). Phone matches on
  // the LAST 7 digits so country-code formatting differences (+1, spaces,
  // dashes) never hide a match; 'guest' matches walk-ins with no name.
  const q = searchQuery.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, '');
  const matchesSearch = (r) => {
    if (q.length < 1) return true;
    const name = (r.customerName || '').toLowerCase();
    if (name.includes(q)) return true;
    if (!name && 'guest'.includes(q)) return true;
    if (qDigits.length >= 4) {
      const phone = (r.customerPhone || '').replace(/\D/g, '');
      const qLast = qDigits.slice(-7);
      const pLast = phone.slice(-7);
      if (qLast.length >= 7 ? pLast === qLast : phone.endsWith(qDigits)) return true;
    }
    return (r.confirmationNumber || '').toLowerCase().includes(q);
  };
  const visibleReservations = reservations.filter(matchesSearch);
  const counts = {
    pending: visibleReservations.filter((r) => r.status === 'pending').length,
    confirmed: visibleReservations.filter((r) => r.status === 'confirmed').length,
    seated: visibleReservations.filter((r) => r.status === 'seated').length,
    completed: visibleReservations.filter((r) => r.status === 'completed').length,
  };

  return (
    <AdminLayout>
      <SetupGuard>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-sm-6">
                <h1 className="m-0">
                  <i className="fas fa-calendar-check mr-2" style={{ color: '#ff6b35' }}></i>
                  Reservations & Queue
                </h1>
              </div>
              <div className="col-sm-6 text-right">
                {viewIndex === 0 && (
                  <button className="btn btn-primary" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={handleNewReservation}>
                    <i className="fas fa-plus mr-1"></i> New reservation
                  </button>
                )}
                {viewIndex === 1 && (
                  <button className="btn btn-primary" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={handleAddToQueue}>
                    <i className="fas fa-user-plus mr-1"></i> Add to queue
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            {/* View toggle */}
            <div className="card">
              <div className="card-body d-flex" style={{ gap: 8, padding: 10 }}>
                <button
                  className={`btn flex-fill ${viewIndex === 0 ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={viewIndex === 0 ? { background: '#ff6b35', borderColor: '#ff6b35' } : {}}
                  onClick={() => setViewIndex(0)}
                >
                  Reservations ({reservations.filter((r) => ['pending', 'confirmed'].includes(r.status)).length})
                </button>
                <button
                  className={`btn flex-fill ${viewIndex === 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={viewIndex === 1 ? { background: '#ff6b35', borderColor: '#ff6b35' } : {}}
                  onClick={() => setViewIndex(1)}
                >
                  Queue ({waitlist.length})
                </button>
              </div>
            </div>

            {viewIndex === 1 ? (
              /* ── Queue view ── */
              waitlist.length === 0 ? (
                <div className="card">
                  <div className="card-body text-center text-muted p-5">
                    <i className="fas fa-list-ol fa-3x mb-3"></i>
                    <p>No parties waiting.</p>
                  </div>
                </div>
              ) : (
                waitlist.map((e, idx) => {
                  const busy = busyId === e.id;
                  const isNotified = e.status === 'notified';
                  return (
                    <div key={e.id} className="card" style={isNotified ? { border: '1.5px solid #28a745' } : {}}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
                          <div className="d-flex align-items-center" style={{ gap: 12 }}>
                            <span
                              className="d-inline-flex align-items-center justify-content-center rounded-circle text-white font-weight-bold"
                              style={{ width: 36, height: 36, background: isNotified ? '#28a745' : '#ff6b35' }}
                            >
                              {idx + 1}
                            </span>
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {e.customerName || 'Guest'}
                                <span className="text-muted ml-2"><i className="fas fa-users mr-1"></i>{e.partySize}</span>
                              </div>
                              <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                                <span className={`badge badge-${isNotified ? 'success' : 'warning'} mr-2`}>{isNotified ? 'Notified' : 'Waiting'}</span>
                                <span className="mr-2"><i className="fas fa-clock mr-1"></i>~{e.quotedWaitMinutes} min</span>
                                {e.createdAt?.toDate && (
                                  <span className="mr-2">arrived {e.createdAt.toDate().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                                )}
                                {e.customerPhone && <span><i className="fas fa-phone mr-1"></i>{e.customerPhone}</span>}
                              </div>
                              {e.notes && (
                                <div className="text-muted" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                                  <i className="fas fa-sticky-note mr-1"></i>{e.notes}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                            {!isNotified && (
                              <button className="btn btn-success btn-sm" disabled={busy} onClick={() => updateWaitlistStatus(e, 'notified')}>
                                <i className="fas fa-bell mr-1"></i>Notify
                              </button>
                            )}
                            <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => handleSeatQueueEntry(e)}>
                              <i className="fas fa-chair mr-1"></i>Seat
                            </button>
                            <button className="btn btn-dark btn-sm" disabled={busy} onClick={() => updateWaitlistStatus(e, 'no_show')}>
                              <i className="fas fa-user-slash mr-1"></i>No-show
                            </button>
                            <button className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => updateWaitlistStatus(e, 'cancelled')}>
                              <i className="fas fa-times mr-1"></i>Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )
            ) : (
              /* ── Reservations view ── */
              <>
            {/* Date navigation */}
            <div className="card">
              <div className="card-body d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 12 }}>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => shiftDay(-1)}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <input
                    type="date"
                    className="form-control"
                    style={{ width: 'auto' }}
                    value={`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`}
                    onChange={(e) => {
                      const d = new Date(`${e.target.value}T12:00:00`);
                      if (!isNaN(d.getTime())) setSelectedDate(d);
                    }}
                  />
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => shiftDay(1)}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                  {!isToday && (
                    <button className="btn btn-link btn-sm" onClick={() => setSelectedDate(new Date())}>
                      Today
                    </button>
                  )}
                </div>
                <div className="d-flex align-items-center" style={{ gap: 16 }}>
                  <input
                    type="text"
                    className="form-control"
                    style={{ width: 220 }}
                    placeholder="🔍 Search name, phone or code…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <span className="badge badge-warning p-2">Pending: {counts.pending}</span>
                  <span className="badge badge-success p-2">Confirmed: {counts.confirmed}</span>
                  <span className="badge badge-primary p-2">Seated: {counts.seated}</span>
                  <span className="badge badge-secondary p-2">Completed: {counts.completed}</span>
                </div>
              </div>
            </div>

            {/* List */}
            {loading ? (
              <div className="text-center p-5">
                <i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#ff6b35' }}></i>
              </div>
            ) : visibleReservations.length === 0 ? (
              <div className="card">
                <div className="card-body text-center text-muted p-5">
                  <i className="fas fa-calendar-times fa-3x mb-3"></i>
                  <p>{q.length >= 1 ? 'No reservations match your search.' : 'No reservations for this date.'}</p>
                </div>
              </div>
            ) : (
              visibleReservations.map((r) => {
                const dt = r.dateTime?.toDate?.();
                const busy = busyId === r.id;
                const prepaid = r.paymentStatus === 'prepaid';
                const refunded = r.paymentStatus === 'refunded';
                return (
                  <div key={r.id} className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
                        <div style={{ minWidth: 110 }}>
                          <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>
                            {dt ? dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                          </div>
                          <span className={`badge badge-${STATUS_COLORS[r.status] || 'secondary'}`}>
                            {STATUS_LABELS[r.status] || r.status}
                          </span>
                        </div>
                        <div className="flex-grow-1">
                          <div style={{ fontWeight: 600 }}>
                            {r.customerName || 'Guest'}
                            <span className="text-muted ml-2">
                              <i className="fas fa-users mr-1"></i>{r.partySize}
                            </span>
                            {prepaid && <span className="badge badge-success ml-2">Prepaid</span>}
                            {refunded && <span className="badge badge-danger ml-2">Refunded</span>}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                            {r.customerPhone && <span className="mr-3"><i className="fas fa-phone mr-1"></i>{r.customerPhone}</span>}
                            {r.customerEmail && <span className="mr-3"><i className="fas fa-envelope mr-1"></i>{r.customerEmail}</span>}
                            <span className="mr-3"><i className="fas fa-hashtag mr-1"></i>{r.confirmationNumber}</span>
                            {r.tableName && <span className="mr-3"><i className="fas fa-chair mr-1"></i>{r.tableName}</span>}
                            {r.createdAt?.toDate && (
                              <span><i className="fas fa-plus-circle mr-1"></i>Created {r.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {r.createdAt.toDate().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                            )}
                          </div>
                          {r.notes && (
                            <div className="text-muted" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                              <i className="fas fa-sticky-note mr-1"></i>{r.notes}
                            </div>
                          )}
                          {Array.isArray(r.preorderItems) && r.preorderItems.length > 0 && (
                            <div className="mt-1" style={{ fontSize: '0.85rem' }}>
                              <i className="fas fa-utensils mr-1 text-muted"></i>
                              Pre-order: {r.preorderItems.map((it) => `${it.productName} ×${it.qty}`).join(', ')}
                              {' '}(${Number(r.preorderTotal || 0).toFixed(2)})
                            </div>
                          )}
                        </div>
                        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                          {r.status === 'pending' && (
                            <button className="btn btn-success btn-sm" disabled={busy} onClick={() => updateStatus(r, 'confirmed')}>
                              <i className="fas fa-check mr-1"></i>Confirm
                            </button>
                          )}
                          {r.status === 'confirmed' && (
                            <>
                              <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => handleSeat(r)}>
                                <i className="fas fa-chair mr-1"></i>Seat
                              </button>
                              <button className="btn btn-dark btn-sm" disabled={busy} onClick={() => handleNoShow(r)}>
                                <i className="fas fa-user-slash mr-1"></i>No-show
                              </button>
                            </>
                          )}
                          {r.status === 'seated' && (
                            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => updateStatus(r, 'completed')}>
                              <i className="fas fa-flag-checkered mr-1"></i>Complete
                            </button>
                          )}
                          {['pending', 'confirmed'].includes(r.status) && (
                            <button className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => handleCancel(r)}>
                              <i className="fas fa-times mr-1"></i>Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
              </>
            )}
          </div>
        </section>
        {seatTarget && (
          <SeatModal
            reservation={seatTarget}
            dayReservations={reservations}
            businessId={user.uid}
            locationId={locationId}
            onClose={() => setSeatTarget(null)}
            onSeat={async (tableId, tableName) => {
              const target = seatTarget;
              setSeatTarget(null);
              if (target._isQueueEntry) {
                await _seatQueueEntryWithTab(target, tableId, tableName);
              } else {
                await _seatReservationWithTab(target, tableId, tableName);
              }
            }}
          />
        )}
        {showNewReservation && (
          <NewReservationModal
            businessId={user.uid}
            onClose={() => setShowNewReservation(false)}
            onCreate={async (formValues) => {
              setShowNewReservation(false);
              await createWalkInReservation(formValues);
            }}
          />
        )}
      </SetupGuard>
    </AdminLayout>
  );
};

/**
 * Floor-plan table picker for seating a reservation — same data flow as the
 * open-tabs table selector: sections + tables, reserved tables (overlapping
 * another active reservation's window) and occupied tables (open tabs) are
 * shown disabled, with an auto-assign shortcut (smallest fitting free
 * table, via the suggestReservationTable callable).
 */
const SeatModal = ({ reservation, dayReservations, businessId, locationId, onClose, onSeat }) => {
  const db = getFirestore();
  const functions = getFunctions();
  const [sections, setSections] = useState([]);
  const [tables, setTables] = useState([]);
  const [occupied, setOccupied] = useState(new Set());
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [customName, setCustomName] = useState(reservation.tableName || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [secSnap, tblSnap, tabsSnap] = await Promise.all([
          getDocs(query(collection(db, 'users', businessId, 'locations', locationId, 'sections'), orderBy('order'))),
          getDocs(collection(db, 'users', businessId, 'locations', locationId, 'tables')),
          getDocs(query(
            collection(db, 'users', businessId, 'openTabs'),
            where('locationId', '==', locationId),
            where('status', 'in', ['open', 'card_presenting', 'card_presented'])
          )),
        ]);
        setSections(secSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setTables(tblSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setOccupied(new Set(tabsSnap.docs.map((d) => d.data().tableId).filter(Boolean)));
      } catch (e) {
        console.error('Error loading floor plan:', e);
      }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reserved: other active reservations with an assigned table overlapping
  // THIS reservation's window.
  const rStart = reservation.dateTime?.toDate?.();
  const rEnd = reservation.endDateTime?.toDate?.() || rStart;
  const reservedByOther = {};
  if (rStart) {
    for (const o of dayReservations) {
      if (o.id === reservation.id) continue;
      if (!['pending', 'confirmed', 'seated'].includes(o.status)) continue;
      const oStart = o.dateTime?.toDate?.();
      const oEnd = o.endDateTime?.toDate?.() || oStart;
      if (!oStart || !o.tableId) continue;
      if (oStart < rEnd && oEnd > rStart) {
        reservedByOther[o.tableId] = `Reserved ${oStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
      }
    }
  }

  const autoAssign = async () => {
    try {
      const fn = httpsCallable(functions, 'suggestReservationTable');
      const { data } = await fn({
        businessId,
        locationId,
        dateTime: rStart?.toISOString() || new Date().toISOString(),
        partySize: reservation.partySize || 2,
        excludeReservationId: reservation.id,
        includeOpenTabs: true,
      });
      if (data.suggestion) {
        setSelectedTable({ id: data.suggestion.tableId, name: data.suggestion.name });
        setCustomName('');
      } else {
        Swal.fire('No free table', 'No free table fits this party right now', 'info');
      }
    } catch (e) {
      Swal.fire('Error', 'Could not compute a suggestion', 'error');
    }
  };

  const visibleTables = selectedSection
    ? tables.filter((t) => t.sectionId === selectedSection)
    : tables;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <div className="card" style={{ width: 640, maxWidth: '100%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            Seat {reservation.customerName || 'party'} · {reservation.partySize} guests
          </h5>
          <button className="btn btn-sm btn-link" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center p-4"><i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#ff6b35' }}></i></div>
          ) : tables.length === 0 ? (
            <p className="text-muted">No floor plan configured for this location — type a table name below.</p>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                  <button
                    className={`btn btn-sm ${selectedSection === null ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={selectedSection === null ? { background: '#ff6b35', borderColor: '#ff6b35' } : {}}
                    onClick={() => setSelectedSection(null)}
                  >All</button>
                  {sections.map((s) => (
                    <button
                      key={s.id}
                      className={`btn btn-sm ${selectedSection === s.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={selectedSection === s.id ? { background: '#ff6b35', borderColor: '#ff6b35' } : {}}
                      onClick={() => setSelectedSection(s.id)}
                    >{s.name}</button>
                  ))}
                </div>
                <button className="btn btn-sm btn-outline-warning" onClick={autoAssign}>
                  <i className="fas fa-magic mr-1"></i>Auto-assign
                </button>
              </div>

              {/* Visual floor plan (same layout as PosPage.js / pos-machine):
                  tables positioned by positionX/positionY on a grid canvas.
                  Red = occupied (open tab) · Blue = reserved (overlapping
                  booking) · Orange = free · Green = selected. */}
              <div
                className="card mb-3"
                style={{
                  height: 360,
                  background: '#f8f9fa',
                  backgroundImage: 'linear-gradient(#e0e0e0 1px, transparent 1px), linear-gradient(90deg, #e0e0e0 1px, transparent 1px)',
                  backgroundSize: '50px 50px',
                  position: 'relative',
                  overflow: 'auto',
                }}
              >
                {visibleTables.length === 0 ? (
                  <div className="d-flex flex-column justify-content-center align-items-center h-100">
                    <i className="fas fa-table fa-3x text-muted mb-2"></i>
                    <p className="text-muted mb-0">No tables in this section</p>
                  </div>
                ) : (
                  <div style={{ position: 'relative', width: 800, height: 600 }}>
                    {visibleTables.map((t) => {
                      const reservedLabel = reservedByOther[t.id];
                      const isOccupied = occupied.has(t.id);
                      const blocked = reservedLabel != null || isOccupied;
                      const selected = selectedTable?.id === t.id;
                      const size = t.shape === 'rectangle' ? { w: 80, h: 50 } : { w: 60, h: 60 };
                      return (
                        <div
                          key={t.id}
                          style={{
                            position: 'absolute',
                            left: t.positionX || 50,
                            top: t.positionY || 50,
                            width: size.w,
                            height: size.h,
                            background: isOccupied
                              ? '#dc3545'
                              : reservedLabel != null
                                ? '#0d6efd'
                                : selected
                                  ? '#28a745'
                                  : '#ff6b35',
                            borderRadius: t.shape === 'rounded' ? '50%' : t.shape === 'triangle' ? '0' : '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: blocked ? 'not-allowed' : 'pointer',
                            userSelect: 'none',
                            color: 'white',
                            fontSize: 10,
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            opacity: blocked ? 0.75 : 1,
                          }}
                          onClick={() => {
                            if (blocked) return;
                            setSelectedTable(selected ? null : { id: t.id, name: t.name });
                            if (!selected) setCustomName('');
                          }}
                          title={`${t.name}${t.maxPartySize ? ` (fits ${t.maxPartySize})` : ''}${isOccupied ? ' — Occupied' : reservedLabel ? ` — ${reservedLabel}` : ''}`}
                        >
                          <span>{t.name}</span>
                          {isOccupied && <span style={{ fontSize: 8 }}>Occupied</span>}
                          {!isOccupied && reservedLabel && <span style={{ fontSize: 8 }}>{reservedLabel}</span>}
                          {!isOccupied && !reservedLabel && t.maxPartySize != null && (
                            <span style={{ fontSize: 8, opacity: 0.9 }}>{t.maxPartySize}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
          <input
            className="form-control"
            placeholder="Or type a table name (optional) — e.g. T4, Patio 2…"
            value={customName}
            onChange={(e) => {
              setCustomName(e.target.value);
              if (e.target.value) setSelectedTable(null);
            }}
          />
        </div>
        <div className="card-footer d-flex justify-content-end" style={{ gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Back</button>
          <button
            className="btn btn-primary"
            style={{ background: '#ff6b35', borderColor: '#ff6b35' }}
            onClick={() => onSeat(selectedTable?.id || null, selectedTable?.name || customName.trim() || null)}
          >
            <i className="fas fa-chair mr-1"></i>Seat
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReservationsPage;

/* ==========================================
   NEW RESERVATION MODAL (SalesPage-style)
   Customer section with searchable selector (registered customers linked by
   their fodyesUid — mirrors/push live under the consumer's auth uid, NOT
   the CRM doc id) + reservation details section.
   ========================================== */
const NewReservationModal = ({ businessId, onClose, onCreate }) => {
  const db = getFirestore();
  const [customers, setCustomers] = useState([]);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // CRM doc or null = guest
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [party, setParty] = useState(2);
  const [date, setDate] = useState(() => {
    const d = new Date(Date.now() + 2 * 3600 * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [time, setTime] = useState(() => {
    const d = new Date(Date.now() + 2 * 3600 * 1000);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  });
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'users', businessId, 'customers'),
          orderBy('name'),
          limit(300)
        ));
        setCustomers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.warn('Could not load customers:', e);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectCustomer = (c) => {
    setSelectedCustomer(c);
    setName(c.name || '');
    setPhone(c.phone || '');
    setEmail(c.email || '');
    setShowCustomerSelector(false);
  };

  const handleSetGuest = () => {
    setSelectedCustomer(null);
    setName('');
    setPhone('');
    setEmail('');
    setShowCustomerSelector(false);
  };

  const handleCreate = () => {
    if (!name.trim()) return setError('Name is required');
    if (!party || party < 1) return setError('Party size is required');
    if (!date || !time) return setError('Date & time are required');
    onCreate({
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim(),
      party,
      time: new Date(`${date}T${time}`).toISOString(),
      notes: notes.trim(),
      // Link the CONSUMER's auth uid (fodyesUid) — mirrors and push tokens
      // live under users/{authUid}, not the CRM doc id.
      customerUid: selectedCustomer?.fodyesUid || null,
    });
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ borderRadius: 16 }}>
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">
              <i className="fas fa-calendar-plus mr-2" style={{ color: '#ff6b35' }}></i>
              New reservation
            </h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Customer section */}
            <div className="card mb-3" style={{ borderRadius: 12 }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="font-weight-bold m-0">Customer</h6>
                  <button className="btn btn-sm btn-outline-primary" onClick={() => setShowCustomerSelector(true)}>
                    {selectedCustomer ? 'Change' : 'Select'}
                  </button>
                </div>
                {selectedCustomer ? (
                  <div>
                    <div className="font-weight-bold">{selectedCustomer.name}</div>
                    <div>{selectedCustomer.email}</div>
                    <div className="text-muted">{selectedCustomer.phone}</div>
                    {selectedCustomer.fodyesUid && (
                      <small className="badge badge-success mt-1">FodyFee member — gets app notifications</small>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="form-group mb-2">
                      <label>Name *</label>
                      <input type="text" className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className="form-group mb-2">
                      <label>Phone (for SMS confirmation)</label>
                      <input type="tel" className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} />
                    </div>
                    <div className="form-group mb-0">
                      <label>Email</label>
                      <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Details section */}
            <div className="card mb-2" style={{ borderRadius: 12 }}>
              <div className="card-body">
                <h6 className="font-weight-bold mb-3">Reservation details</h6>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <label className="m-0 font-weight-bold">Party size</label>
                  <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    <button className="btn btn-outline-secondary btn-sm rounded-circle" style={{ width: 34, height: 34 }}
                      onClick={() => setParty(Math.max(1, party - 1))}>−</button>
                    <strong style={{ fontSize: '1.1rem' }}>{party}</strong>
                    <button className="btn btn-outline-secondary btn-sm rounded-circle" style={{ width: 34, height: 34 }}
                      onClick={() => setParty(party + 1)}>+</button>
                  </div>
                </div>
                <div className="row">
                  <div className="col-7">
                    <div className="form-group mb-2">
                      <label>Date *</label>
                      <input type="date" className="form-control" value={date} onChange={(e) => setDate(e.target.value)} />
                    </div>
                  </div>
                  <div className="col-5">
                    <div className="form-group mb-2">
                      <label>Time *</label>
                      <input type="time" className="form-control" value={time} onChange={(e) => setTime(e.target.value)} />
                    </div>
                  </div>
                </div>
                <div className="form-group mb-0">
                  <label>Notes</label>
                  <textarea className="form-control" rows="2" placeholder="Special requests…" value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
              </div>
            </div>

            {error && <div className="alert alert-danger py-2">{error}</div>}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={handleCreate}>
              <i className="fas fa-check mr-1"></i>Create reservation
            </button>
          </div>
        </div>
      </div>

      {showCustomerSelector && (
        <ReservationCustomerSelector
          customers={customers}
          onSelect={handleSelectCustomer}
          onGuest={handleSetGuest}
          onClose={() => setShowCustomerSelector(false)}
        />
      )}
    </div>
  );
};

/* Customer selector — same pattern as SalesPage's CustomerSelectorModal:
   search by name/email/phone, list of registered customers, Guest option. */
const ReservationCustomerSelector = ({ customers, onSelect, onGuest, onClose }) => {
  const [search, setSearch] = useState('');
  const filtered = customers.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search)
  );

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1070 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ borderRadius: 16 }}>
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">Select Customer</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search customers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button className="btn btn-outline-secondary ml-2" onClick={onGuest}>Guest</button>
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {filtered.map((c) => (
                <button key={c.id} className="list-group-item list-group-item-action py-2 text-left" onClick={() => onSelect(c)}>
                  <div className="font-weight-bold">
                    {c.name}
                    {c.fodyesUid && <i className="fas fa-check-circle text-success ml-2" title="FodyFee member"></i>}
                  </div>
                  <small className="text-muted">{c.email}{c.email && c.phone ? ' · ' : ''}{c.phone}</small>
                </button>
              ))}
              {filtered.length === 0 && <div className="text-center text-muted py-3">No customers found</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
